import {
  careCallTypeSchema,
  type CareCallType,
  type RoutableCareCallType,
  routableCareCallTypeSchema,
} from './care-call-types';

/** The suffix after `appointment.type` in the care catalogue for a call's pill. */
export type CareAppointmentTypeLabelSuffix =
  | 'Welcome'
  | 'TreatmentPlan'
  | 'ProviderCall'
  | 'FollowUp'
  | 'MemberSupport'
  | 'ProgressPurposeCheckin'
  | 'Label';

function suffixFor(callType: CareCallType): CareAppointmentTypeLabelSuffix {
  switch (callType) {
    case 'welcome_call_substance':
    case 'welcome_call_tobacco':
    case 'welcome_call_behavioral':
    case 'welcome_call_low_no':
    case 'welcome_call_sud':
      return 'Welcome';
    case 'treatment_plan':
      return 'TreatmentPlan';
    case 'provider_call':
      return 'ProviderCall';
    case 'follow_up':
      return 'FollowUp';
    case 'member_support':
      return 'MemberSupport';
    case 'progress_purpose_checkin':
      return 'ProgressPurposeCheckin';
    case 'behavioural_health':
      return 'Label';
    default: {
      // A new call type fails compilation here rather than silently falling
      // through to the generic pill on Home and the Care tab.
      const unhandled: never = callType;
      return unhandled;
    }
  }
}

/**
 * Which `appointment.type*` suffix labels a booked appointment's call type. An
 * absent or unrecognised value (an older server, a legacy row) reads as the
 * generic care-specialist label.
 */
export function careAppointmentTypeLabelSuffix(
  callType: string | null | undefined,
): CareAppointmentTypeLabelSuffix {
  const parsed = careCallTypeSchema.safeParse(callType);
  return parsed.success ? suffixFor(parsed.data) : 'Label';
}

/**
 * Which care-catalog key labels a booked appointment's call type.
 *
 * Shared between the mobile care surfaces and the server-authored home
 * document so the pill on the care tab and the leading home card never drift.
 */
export function careAppointmentTypeCatalogKey(callType: string | null | undefined): string {
  return `appointment.type${careAppointmentTypeLabelSuffix(callType)}`;
}

/**
 * English member-facing appointment type labels from
 * `apps/api/src/i18n/catalog/en/care.json` — used as Healthie appointment type names (ADR-017).
 */
export const HEALTHIE_APPOINTMENT_TYPE_ENGLISH_LABELS = {
  welcome_call_substance: 'Welcome call',
  welcome_call_tobacco: 'Welcome call',
  welcome_call_behavioral: 'Welcome call',
  welcome_call_low_no: 'Welcome call',
  treatment_plan: 'Treatment plan call',
  provider_call: 'Provider call',
  follow_up: 'Follow-up call',
  member_support: 'Member support call',
  progress_purpose_checkin: 'Progress & Purpose check-in',
} as const satisfies Record<RoutableCareCallType, string>;

/**
 * Unique English Healthie appointment type names for deploy sync (one row → one
 * type). Member-facing pills still use {@link HEALTHIE_APPOINTMENT_TYPE_ENGLISH_LABELS}.
 */
export const HEALTHIE_APPOINTMENT_TYPE_SYNC_BASE_NAMES = {
  welcome_call_substance: 'Welcome call (substance)',
  welcome_call_tobacco: 'Welcome call (tobacco)',
  welcome_call_behavioral: 'Welcome call (behavioural)',
  welcome_call_low_no: 'Welcome call (low/no acuity AUD)',
  treatment_plan: 'Treatment plan call',
  provider_call: 'Provider call',
  follow_up: 'Follow-up call',
  member_support: 'Member support call',
  progress_purpose_checkin: 'Progress & Purpose check-in',
} as const satisfies Record<RoutableCareCallType, string>;

/** Stable Healthie appointment type base name for a routable call type. */
export function healthieAppointmentTypeEnglishLabel(callType: RoutableCareCallType): string {
  return HEALTHIE_APPOINTMENT_TYPE_ENGLISH_LABELS[callType];
}

/** Canonical Healthie type name used by deploy sync (unique per routable call type). */
export function healthieAppointmentTypeSyncBaseName(callType: RoutableCareCallType): string {
  return HEALTHIE_APPOINTMENT_TYPE_SYNC_BASE_NAMES[callType];
}

/** Prefix every Galapagos-owned Healthie appointment type carries (never matches 1.0-era names). */
export const GALAPAGOS_HEALTHIE_APPOINTMENT_TYPE_PREFIX = 'Galapagos - ' as const;

/**
 * Dev Healthie appointment type used for org-wide availability fallback — deploy sync must never
 * mutate this record (link-only when a policy row still references it).
 */
export const HEALTHIE_AVAILABILITY_FALLBACK_APPOINTMENT_TYPE_ID = '543652' as const;

export {
  EPHEMERAL_PREVIEW_ENV_KEY_PATTERN,
  GALAPAGOS_PREVIEW_HEALTHIE_NAME_SUFFIX_PATTERN,
  isEphemeralPreviewEnvKey,
} from './preview-env-key-pattern';

import { GALAPAGOS_PREVIEW_HEALTHIE_NAME_SUFFIX_PATTERN } from './preview-env-key-pattern';

const PREVIEW_SUFFIX_STRIP = GALAPAGOS_PREVIEW_HEALTHIE_NAME_SUFFIX_PATTERN;

/** Full Healthie name the deploy sync creates, updates, and matches (excludes preview suffix). */
export function galapagosPrefixedHealthieSyncName(baseSyncName: string): string {
  return `${GALAPAGOS_HEALTHIE_APPOINTMENT_TYPE_PREFIX}${baseSyncName}`;
}

export function galapagosPrefixedHealthieSyncNameForCallType(
  callType: RoutableCareCallType,
): string {
  return galapagosPrefixedHealthieSyncName(healthieAppointmentTypeSyncBaseName(callType));
}

/**
 * Base sync name for one length of a call type: unchanged when the call type
 * offers a single length, `<base> (<minutes> min)` when it offers several, so
 * each Healthie appointment type carries a distinct name.
 */
export function healthieAppointmentTypeSyncNameForOption(
  callType: RoutableCareCallType,
  minutes: number,
  optionCount: number,
): string {
  const base = healthieAppointmentTypeSyncBaseName(callType);
  return optionCount > 1 ? `${base} (${minutes} min)` : base;
}

/** Full Galapagos-prefixed Healthie name for one length of a call type. */
export function galapagosPrefixedHealthieSyncNameForOption(
  callType: RoutableCareCallType,
  minutes: number,
  optionCount: number,
): string {
  return galapagosPrefixedHealthieSyncName(
    healthieAppointmentTypeSyncNameForOption(callType, minutes, optionCount),
  );
}

/** True when the type name is Galapagos sync-owned (prefix present, with or without preview suffix). */
export function isGalapagosOwnedHealthieTypeName(name: string): boolean {
  const withoutSuffix = name.trim().replace(PREVIEW_SUFFIX_STRIP, '');
  return withoutSuffix.startsWith(GALAPAGOS_HEALTHIE_APPOINTMENT_TYPE_PREFIX);
}

/** Parses a routable call type and returns its Healthie base label, or undefined. */
export function healthieAppointmentTypeEnglishLabelForCallType(
  callType: string,
): string | undefined {
  const parsed = routableCareCallTypeSchema.safeParse(callType);
  return parsed.success ? healthieAppointmentTypeEnglishLabel(parsed.data) : undefined;
}
